/**
 * Booking-provider detection for the inline scheduling section.
 *
 * Each provider has a `match` regex on the URL and a `toEmbed` function that
 * returns the iframe src to use (sometimes the same URL, sometimes a tweaked
 * embed-mode variant). Unknown URLs return `null` for `embed`, and the public
 * card falls back to a "Open booking page" button.
 *
 * Adding a provider is intentionally low-friction: paste a regex, add the
 * brand color, optionally tweak `toEmbed`. The renderer doesn't need changes.
 */

export interface BookingProvider {
  /** Stable id, used as a key. */
  id: string;
  /** Display label shown on the public card. */
  name: string;
  /** Hex color used for the provider pill on the card. */
  color: string;
  /** Does this provider support iframe embedding cleanly? */
  embeddable: boolean;
}

export interface BookingMatch {
  provider: BookingProvider;
  /** iframe src (null if not embeddable — render a button instead). */
  embedSrc: string | null;
  /** The original URL, always usable as an "open in new tab" target. */
  url: string;
}

interface ProviderDef extends BookingProvider {
  pattern: RegExp;
  toEmbed: (url: string) => string | null;
}

const PROVIDERS: ProviderDef[] = [
  {
    id: 'calendly',
    name: 'Calendly',
    color: '#006BFF',
    embeddable: true,
    pattern: /(^|\.)calendly\.com\//i,
    toEmbed: (url) => addQuery(url, { embed_domain: hostnameOrEmpty(), embed_type: 'Inline' }),
  },
  {
    id: 'cal-com',
    name: 'Cal.com',
    color: '#111827',
    embeddable: true,
    pattern: /(^|\.)cal\.com\//i,
    toEmbed: (url) => addQuery(url, { embed: 'true' }),
  },
  {
    id: 'square',
    name: 'Square Appointments',
    color: '#000000',
    embeddable: true,
    pattern: /(book\.squareup\.com|squareup\.com\/appointments|square\.site\/book)/i,
    toEmbed: (url) => url,
  },
  {
    id: 'acuity',
    name: 'Acuity',
    color: '#5B5FC7',
    embeddable: true,
    pattern: /(acuityscheduling\.com|\.as\.me)/i,
    toEmbed: (url) => url,
  },
  {
    id: 'setmore',
    name: 'Setmore',
    color: '#00BF6F',
    embeddable: true,
    pattern: /(^|\.)setmore\.com\//i,
    toEmbed: (url) => url,
  },
  {
    id: 'simplybook',
    name: 'SimplyBook',
    color: '#FF6A00',
    embeddable: true,
    pattern: /(^|\.)simplybook\.me\//i,
    toEmbed: (url) => url,
  },
  {
    id: 'hubspot-meetings',
    name: 'HubSpot Meetings',
    color: '#FF7A59',
    embeddable: true,
    pattern: /meetings\.hubspot\.com\//i,
    toEmbed: (url) => addQuery(url, { embed: 'true' }),
  },
  {
    id: 'microsoft-bookings',
    name: 'Microsoft Bookings',
    color: '#0078D4',
    embeddable: true,
    pattern: /(outlook\.office\.com\/bookwithme|book\.ms\/)/i,
    toEmbed: (url) => url,
  },
  {
    id: 'tidycal',
    name: 'TidyCal',
    color: '#15BBA6',
    embeddable: true,
    pattern: /(^|\.)tidycal\.com\//i,
    toEmbed: (url) => url,
  },
  {
    id: 'youcanbookme',
    name: 'YouCanBook.me',
    color: '#15BBA6',
    embeddable: true,
    pattern: /\.youcanbook\.me\//i,
    toEmbed: (url) => url,
  },
];

/**
 * Detect the booking provider for a URL. Returns null if the URL is empty
 * or malformed (e.g. not a real URL). For unrecognized but valid URLs,
 * returns a generic provider record with `embedSrc: null`, letting the
 * renderer choose to show a button instead of an iframe.
 */
export function detectBooking(url: string | null | undefined): BookingMatch | null {
  if (!url) return null;
  const clean = url.trim();
  if (!clean) return null;
  if (!/^https?:\/\//i.test(clean)) return null;

  for (const p of PROVIDERS) {
    if (p.pattern.test(clean)) {
      return {
        provider: { id: p.id, name: p.name, color: p.color, embeddable: p.embeddable },
        embedSrc: p.embeddable ? p.toEmbed(clean) : null,
        url: clean,
      };
    }
  }

  // Generic — recognized as a URL but not a known provider.
  return {
    provider: { id: 'generic', name: 'Booking', color: '#6B7280', embeddable: false },
    embedSrc: null,
    url: clean,
  };
}

// ── Utilities ────────────────────────────────────────────────────────────────

function addQuery(url: string, params: Record<string, string>): string {
  try {
    const u = new URL(url);
    for (const [k, v] of Object.entries(params)) {
      if (!u.searchParams.has(k)) u.searchParams.set(k, v);
    }
    return u.toString();
  } catch {
    return url;
  }
}

function hostnameOrEmpty(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}
